import { useEffect, useState, useMemo, Fragment } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Alert, Chip, Stack, CircularProgress, Link,
  TextField, InputAdornment, Button, Dialog, DialogTitle, DialogContent, DialogActions,
  Tooltip, Table, TableHead, TableRow, TableCell, TableBody, Paper, IconButton, Collapse, Snackbar,
  Menu, MenuItem, ListItemIcon, ListItemText,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import SyncIcon from '@mui/icons-material/Sync';
import RefreshIcon from '@mui/icons-material/Refresh';
import ErrorIcon from '@mui/icons-material/Error';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import UnfoldLessIcon from '@mui/icons-material/UnfoldLess';
// FEAT-016 v1.2 — module-representative icons for the tier columns.
import DescriptionIcon from '@mui/icons-material/Description';
import GroupIcon from '@mui/icons-material/Group';
import GppGoodIcon from '@mui/icons-material/GppGood';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import VerifiedIcon from '@mui/icons-material/Verified';
import BugReportIcon from '@mui/icons-material/BugReport';
import CampaignIcon from '@mui/icons-material/Campaign';
import PolicyIcon from '@mui/icons-material/Policy';
import AssessmentIcon from '@mui/icons-material/Assessment';
import InsightsIcon from '@mui/icons-material/Insights';
import GavelIcon from '@mui/icons-material/Gavel';
import AlarmIcon from '@mui/icons-material/Alarm';
import SecurityIcon from '@mui/icons-material/Security';
import HubIcon from '@mui/icons-material/Hub';
import ShareIcon from '@mui/icons-material/Share';
import SettingsIcon from '@mui/icons-material/Settings';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import SelfImprovementIcon from '@mui/icons-material/SelfImprovement';
import ExtensionIcon from '@mui/icons-material/Extension';
import { getProductInventory, getProductsSync, getContacts, getProducts, syncSoftware, syncProduct, saveProducts } from '../api/featureMap';
import ContactSelector from '../components/team/ContactSelector';

// category 는 부모 행에서 컬럼으로 노출하지 않지만(FEAT-016), 그룹 정렬 순서는 유지한다.
const categoryOrder = ['core', 'localized', 'utility', 'partner'];

// SW 신호등 (SoftwaresPage 와 동일 표기)
const sigEmoji = { green: '🟢', yellow: '🟡', white: '⚪' };

// 컬럼 구성: 토글 + 제품 + Version + (REDERI Modules: core·recommended·opt-in) + 담당 + 건강도 + 설명 + 작업(FEAT-017)
const TOTAL_COLS = 10;

// FEAT-016 §4.2 — products.sync.yaml badges[] 중 건강도 배지 식별 키.
// v1.2: 건강도 자체는 rederi.yaml health 로 표시하지만, 설명 칸의 배지 목록에서는
// 중복 방지를 위해 계속 제외한다.
const HEALTH_BADGE_ALT = 'REDERI Health';

// FEAT-016 v1.2 §4.4 — rederi.yaml modules 를 tier 별 컬럼으로 노출. 파일에 적힌 tier 그대로
// 그룹핑하되, 알려진 3종은 이 순서로 우선 배치한다.
const TIER_ORDER = ['core', 'recommended', 'opt-in'];

// FEAT-016 v1.2 §4.2 — health.grade → MUI Chip color.
const GRADE_COLOR = { green: 'success', yellow: 'warning', red: 'error' };

// FEAT-016 §4.3 폴백 #2 — 마일스톤이 없을 때 products.yaml roadmap 의 계획 버전.
// in-progress 항목 우선, 없으면 첫 항목.
const roadmapVersionOf = (product) => {
  const rm = Array.isArray(product?.roadmap) ? product.roadmap : [];
  return (rm.find((r) => r.status === 'in-progress') || rm[0])?.version || null;
};

// FEAT-016 v1.2 — 각 모듈을 기능을 대표하는 아이콘으로 표기. 켜짐=색상 / 꺼짐=흐린 회색.
// 새 모듈이 추가되면 매핑에 없어도 기본 아이콘(퍼즐 조각)으로 표시된다.
const MODULE_ICON = {
  // core
  'claude-md-patch': DescriptionIcon,       // CLAUDE.md 지침
  'owners-core': GroupIcon,                 // OWNERS / 담당
  'qms-ssot-guardrails': GppGoodIcon,       // SSOT 가드레일
  'release-core': RocketLaunchIcon,         // 릴리즈
  'release-guides': MenuBookIcon,           // 릴리즈 가이드 문서
  'sdd-core': FactCheckIcon,                // Spec 기반 개발
  'sdd-validation': VerifiedIcon,           // Spec 자동 검증
  'tracking-issue': BugReportIcon,          // 트래킹 이슈
  // recommended
  'console-release-notify': CampaignIcon,   // 릴리즈 알림
  'feature-mirror': SyncIcon,               // Feature 미러 동기화
  'harness-audit': PolicyIcon,              // 하네스 감사
  'harness-eval': AssessmentIcon,           // 하네스 평가
  'harness-telemetry': InsightsIcon,        // 텔레메트리
  'qms-quality-gates': GavelIcon,           // 문서 품질 게이트
  'reminderforge': AlarmIcon,               // 리마인더
  'security-scan': SecurityIcon,            // 보안 스캔
  // opt-in
  'qms-aggregators': HubIcon,               // 집계
  'qms-distribution': ShareIcon,            // 배포/분배
  'qms-operations': SettingsIcon,           // 운영
  'qms-pipeline-core': AccountTreeIcon,     // 파이프라인
  'qms-spec-migration-tools': SwapHorizIcon,// Spec 마이그레이션
  'self-service-mode': SelfImprovementIcon, // 셀프서비스
};

function ModuleIcons({ modules }) {
  if (!modules || modules.length === 0) return <span style={{ color: '#666' }}>—</span>;
  return (
    <Stack direction="row" sx={{ flexWrap: 'wrap', gap: '4px', justifyContent: 'center' }}>
      {modules.map((m) => {
        const Icon = MODULE_ICON[m.id] || ExtensionIcon;
        return (
          <Tooltip key={m.id} title={`${m.id} — ${m.enabled ? '켜짐' : '꺼짐'}`}>
            <Icon
              sx={{
                fontSize: 17,
                color: m.enabled ? 'success.main' : 'action.disabled',
                opacity: m.enabled ? 1 : 0.55,
              }}
            />
          </Tooltip>
        );
      })}
    </Stack>
  );
}

// FEAT-016 v1.2 §4.2 — rederi.yaml health 를 등급 색 + 점수 칩으로. 없으면 —.
function HealthChip({ health }) {
  if (!health || health.total == null) return <span style={{ color: '#666' }}>—</span>;
  const color = GRADE_COLOR[health.grade] || 'default';
  const asOf = health.computed_at ? ` · ${String(health.computed_at).slice(0, 10)}` : '';
  return (
    <Tooltip title={`REDERI 건강도 ${health.total}점 (${health.grade || '?'})${asOf}`}>
      <Chip label={health.total} color={color} size="small" sx={{ fontWeight: 700, minWidth: 40 }} />
    </Tooltip>
  );
}

export default function ProductsPage() {
  const [products, setProducts] = useState([]);
  const [swMap, setSwMap] = useState(new Map());   // swId → SW 상세 (softwares.yaml)
  const [syncMap, setSyncMap] = useState({});      // { [id]: SyncResult }
  const [lastSyncedAt, setLastSyncedAt] = useState(null);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [syncingSw, setSyncingSw] = useState(() => new Set()); // 동기화 중인 SW id
  const [syncingProduct, setSyncingProduct] = useState(() => new Set()); // FEAT-017 개별 동기화 중인 제품 id
  const [excludeTarget, setExcludeTarget] = useState(null); // FEAT-017 관리 제외 확인 대상 제품
  const [excluding, setExcluding] = useState(false);
  const [snackbar, setSnackbar] = useState(null); // { severity, message }
  const [expanded, setExpanded] = useState(() => new Set()); // 펼친 제품 id

  const load = async () => {
    setLoading(true);
    try {
      const [inv, sync, ct, sw] = await Promise.all([
        getProductInventory(), getProductsSync(), getContacts(), getProducts(),
      ]);
      setProducts(inv.products || []);
      const map = {};
      (sync.products || []).forEach((r) => { map[r.id] = r; });
      setSyncMap(map);
      setLastSyncedAt(sync.lastRunAt);
      setMembers(ct.membersFlat || []);
      setSwMap(new Map((sw.products || []).map((s) => [s.id, s])));
    } catch (e) { setError(e.message); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return products;
    return products.filter((p) => {
      const sw = (syncMap[p.id]?.mani_softwares || []).map((s) => s.id).join(' ');
      return `${p.id} ${p.name} ${p.description} ${p.owner} ${sw}`.toLowerCase().includes(q);
    });
  }, [products, search, syncMap]);

  // FEAT-016 §4.1 — 담당 이메일 → 이름 매핑 (contacts SSOT 재사용).
  const nameByEmail = useMemo(() => {
    const m = new Map();
    members.forEach((mem) => { if (mem.email) m.set(mem.email, mem.name); });
    return m;
  }, [members]);

  const handleSync = async () => {
    if (products.length === 0) return;
    setSyncing(true); setError(null);
    try {
      const res = await fetch('/api/products/sync', { method: 'POST' });
      const data = await res.json();
      if (!res.ok || !data.ok) throw new Error(data.error || `sync: ${res.status}`);
      // commit 직후 즉시 UI 갱신 (deploy 전까지 새로고침 시엔 옛 데이터 보일 수 있음)
      const map = {};
      (data.products || []).forEach((r) => { map[r.id] = r; });
      setSyncMap(map);
      setLastSyncedAt(data.last_run_at);
    } catch (e) { setError(e.message); } finally { setSyncing(false); }
  };

  // sync.yaml은 latest_release인데 products.sync.yaml 표준 키. mapping
  const adaptSync = (s) => s ? ({
    exists: s.exists,
    error: s.error,
    desc: s.repo_desc,
    defaultBranch: s.default_branch,
    latest: s.latest_release,
    hasDashboard: s.has_dashboard,
    milestoneVersion: s.milestone_version,
    badges: s.badges,
    rederi: s.rederi,
    maniSoftwares: s.mani_softwares,
  }) : null;

  // category 순 → id 순 정렬 (기존 카드 뷰의 그룹 순서 유지)
  const sorted = useMemo(() => {
    const rank = (c) => {
      const i = categoryOrder.indexOf(c || 'core');
      return i === -1 ? categoryOrder.length : i;
    };
    return [...filtered].sort((a, b) => rank(a.category) - rank(b.category) || a.id.localeCompare(b.id));
  }, [filtered]);

  const toggle = (id) => setExpanded((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  // 개별 SW 동기화 (FEAT-011 이식). SW id 별 로딩 격리 — 다른 SW 와 병렬 허용.
  const handleSyncSw = async (swId) => {
    if (!swId || syncingSw.has(swId)) return;
    setSyncingSw((prev) => new Set(prev).add(swId));
    try {
      const result = await syncSoftware(swId);
      if (result?.ok) {
        const changedSuffix = result.changed === false ? ' (변경 없음)' : '';
        setSnackbar({ severity: 'success', message: `동기화 완료: ${swId}${changedSuffix}` });
        if (result.changed !== false) load();
      } else {
        setSnackbar({ severity: 'error', message: `동기화 실패: ${result?.error || '알 수 없는 오류'}` });
      }
    } catch (e) {
      setSnackbar({ severity: 'error', message: `동기화 실패: ${e.message}` });
    } finally {
      setSyncingSw((prev) => { const next = new Set(prev); next.delete(swId); return next; });
    }
  };

  // FEAT-017 — 개별 제품 동기화. 제품 id 별 로딩 격리.
  const handleSyncProduct = async (id) => {
    if (!id || syncingProduct.has(id)) return;
    setSyncingProduct((prev) => new Set(prev).add(id));
    try {
      const data = await syncProduct(id);
      const updated = (data.products || [])[0];
      if (updated) {
        setSyncMap((prev) => ({ ...prev, [id]: updated }));
        if (data.last_run_at) setLastSyncedAt(data.last_run_at);
      }
      setSnackbar({ severity: 'success', message: `동기화 완료: ${id}` });
    } catch (e) {
      setSnackbar({ severity: 'error', message: `동기화 실패: ${e.message}` });
    } finally {
      setSyncingProduct((prev) => { const next = new Set(prev); next.delete(id); return next; });
    }
  };

  // FEAT-017 — 관리 제외(하드 제거). products.yaml 에서 해당 제품 항목 제거 후 재저장.
  const handleExcludeConfirm = async () => {
    const target = excludeTarget;
    if (!target) return;
    setExcluding(true);
    try {
      const next = products.filter((p) => p.id !== target.id);
      await saveProducts(next, `chore(products): 관리 제외 — ${target.id} (FEAT-017)`);
      setExcludeTarget(null);
      setSnackbar({ severity: 'success', message: `관리에서 제외됨: ${target.id}` });
      load();
    } catch (e) {
      setSnackbar({ severity: 'error', message: `제외 실패: ${e.message}` });
    } finally {
      setExcluding(false);
    }
  };

  // 전체 펼치기/접기 — 파생 SW(mani.yaml)를 가진 제품만 대상
  const expandableIds = useMemo(
    () => sorted.filter((p) => (syncMap[p.id]?.mani_softwares || []).length > 0).map((p) => p.id),
    [sorted, syncMap],
  );
  const allExpanded = expandableIds.length > 0 && expandableIds.every((id) => expanded.has(id));
  const toggleAll = () => setExpanded(allExpanded ? new Set() : new Set(expandableIds));

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
        <Typography variant="h2">제품 · 소프트웨어 (Products &amp; Software)</Typography>
        <Stack direction="row" spacing={1}>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setDialogOpen(true)}>제품 등록</Button>
          <Button startIcon={syncing ? <CircularProgress size={16} /> : <SyncIcon />} onClick={handleSync} disabled={syncing}>
            {syncing ? '전체 Sync 중...' : '전체 Sync'}
          </Button>
        </Stack>
      </Stack>
      <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 2 }}>
        제품 행을 펼치면 포함된 소프트웨어(SW)가 표시됩니다.
        총 {products.length}건 (필터: {filtered.length}건)
        {lastSyncedAt && <> · 마지막 동기화: {lastSyncedAt.slice(0, 16).replace('T', ' ')}</>}
      </Typography>

      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
        <TextField
          size="small"
          placeholder="검색: id, name, owner, SW..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ width: 360 }}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
        />
        <Button
          size="small"
          startIcon={allExpanded ? <UnfoldLessIcon /> : <UnfoldMoreIcon />}
          onClick={toggleAll}
          disabled={expandableIds.length === 0}
        >
          {allExpanded ? '전체 접기' : '전체 펼치기'}
        </Button>
      </Stack>

      {loading && <CircularProgress size={28} />}
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      {!loading && (
        <Paper>
          <Table size="small">
            <TableHead
              sx={{
                bgcolor: 'action.hover',
                // 헤더 행을 데이터 행과 뚜렷이 구분 — 배경 음영 + 모든 헤더 셀 하단 굵은 경계선.
                '& .MuiTableCell-root': {
                  textTransform: 'uppercase',
                  fontWeight: 700,
                  borderBottom: 2,
                  borderColor: 'divider',
                },
              }}
            >
              <TableRow>
                <TableCell sx={{ width: 40 }} rowSpan={2} />
                <TableCell rowSpan={2}>제품</TableCell>
                <TableCell sx={{ width: 90 }} rowSpan={2}>Version</TableCell>
                <Tooltip title="이 제품이 도입한 REDERI 구성 요소 — 등급(tier)별로 켜짐/꺼짐 표시">
                  <TableCell
                    colSpan={3}
                    align="center"
                    sx={{ borderLeft: 1, borderRight: 1, borderColor: 'divider', letterSpacing: 0.5, py: 0.5 }}
                  >
                    REDERI Modules
                  </TableCell>
                </Tooltip>
                <TableCell sx={{ width: 100 }} rowSpan={2}>담당</TableCell>
                <TableCell sx={{ width: 80, textAlign: 'center' }} rowSpan={2}>건강도</TableCell>
                <TableCell rowSpan={2}>설명</TableCell>
                <TableCell sx={{ width: 48 }} rowSpan={2} />
              </TableRow>
              <TableRow>
                <Tooltip title="필수 구성 요소 — REDERI 도입 시 항상 켜짐 (기준선)">
                  <TableCell align="center" sx={{ width: 96, borderLeft: 1, borderColor: 'divider', fontSize: 11, color: 'text.secondary' }}>core</TableCell>
                </Tooltip>
                <Tooltip title="권장 구성 요소 — 실제 도입 수준 차이가 가장 잘 드러남">
                  <TableCell align="center" sx={{ width: 96, fontSize: 11, color: 'text.secondary' }}>recommended</TableCell>
                </Tooltip>
                <Tooltip title="완전 선택 구성 요소 — 필요한 제품만 켬">
                  <TableCell align="center" sx={{ width: 90, borderRight: 1, borderColor: 'divider', fontSize: 11, color: 'text.secondary' }}>opt-in</TableCell>
                </Tooltip>
              </TableRow>
            </TableHead>
            <TableBody>
              {sorted.map((p) => (
                <ProductRow
                  key={p.id}
                  product={p}
                  sync={adaptSync(syncMap[p.id])}
                  swMap={swMap}
                  nameByEmail={nameByEmail}
                  open={expanded.has(p.id)}
                  onToggle={() => toggle(p.id)}
                  syncingSw={syncingSw}
                  onSyncSw={handleSyncSw}
                  syncingProduct={syncingProduct.has(p.id)}
                  onSyncProduct={handleSyncProduct}
                  onExclude={setExcludeTarget}
                />
              ))}
            </TableBody>
          </Table>
        </Paper>
      )}

      <RegisterDialog open={dialogOpen} onClose={() => setDialogOpen(false)} members={members} onSuccess={load} />

      {/* FEAT-017 — 관리 제외(하드 제거) 확인 다이얼로그 */}
      <Dialog open={!!excludeTarget} onClose={() => !excluding && setExcludeTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle>관리에서 제외</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            <b>{excludeTarget?.name}</b> (<code>{excludeTarget?.id}</code>) 을(를) 관리 목록에서 완전히 제거합니다.
          </Typography>
          <Alert severity="warning" sx={{ mt: 2 }}>
            되돌릴 수 없습니다. 다시 관리하려면 "제품 등록"으로 재등록해야 합니다.
            (제품 저장소 자체는 삭제되지 않으며, 목록에서만 제거됩니다.)
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setExcludeTarget(null)} disabled={excluding}>취소</Button>
          <Button color="error" variant="contained" onClick={handleExcludeConfirm} disabled={excluding}>
            {excluding ? '제외 중...' : '제외'}
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={!!snackbar}
        autoHideDuration={4000}
        onClose={() => setSnackbar(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {snackbar ? (
          <Alert severity={snackbar.severity} onClose={() => setSnackbar(null)} sx={{ width: '100%' }}>
            {snackbar.message}
          </Alert>
        ) : undefined}
      </Snackbar>
    </Box>
  );
}

function ProductRow({ product: p, sync, swMap, nameByEmail, open, onToggle, syncingSw, onSyncSw, syncingProduct, onSyncProduct, onExclude }) {
  const navigate = useNavigate();
  const [menuEl, setMenuEl] = useState(null);
  // FEAT-004: row 전체 클릭 → /products/:id. 토글/GitHub Link 클릭은 stopPropagation 으로 라우팅 차단.
  const handleClick = () => navigate(`/products/${p.id}`);
  const stopProp = (e) => e.stopPropagation();
  // FEAT-017 — SW 목록은 제품 구성 정의(mani.yaml) 파생. 수동 매핑(p.softwares) 대신 sync 캐시 사용.
  const maniSw = sync?.maniSoftwares || [];
  const hasSw = maniSw.length > 0;

  // FEAT-016 §4.1 — 담당 이메일 → 이름. 매칭 실패 시 이메일 그대로.
  const ownerDisplay = (p.owner && nameByEmail?.get(p.owner)) || p.owner || 'TBD';
  // FEAT-016 §4.3 — Latest Version = 마일스톤 기준 버전, 폴백: 로드맵 계획 버전 → 빈 칸.
  const milestone = sync?.milestoneVersion;
  const roadmapVersion = milestone ? null : roadmapVersionOf(p);
  // FEAT-016 v1.2 §4.4 — tier 별 도입 신호 (rederi.yaml modules).
  const rederiModules = sync?.rederi?.modules || [];
  const modulesByTier = (tier) => rederiModules.filter((m) => m.tier === tier);
  // FEAT-016 v1.2 §4.2 — 건강도는 rederi.yaml health(점수·등급) 로 표시.
  const health = sync?.rederi?.health || null;
  // 설명 칸 배지에서는 (구) 건강도 배지 제외 — 건강도는 전용 칩으로 표시.
  const otherBadges = (sync?.badges || []).filter((b) => b.alt !== HEALTH_BADGE_ALT);

  return (
    <Fragment>
      <TableRow hover onClick={handleClick} sx={{ cursor: 'pointer', '& > *': { borderBottom: open ? 'unset' : undefined } }}>
        <TableCell onClick={stopProp} sx={{ cursor: hasSw ? 'pointer' : 'default' }}>
          {hasSw && (
            <IconButton size="small" onClick={onToggle} aria-label={open ? '접기' : '펼치기'}>
              {open ? <KeyboardArrowDownIcon fontSize="small" /> : <KeyboardArrowRightIcon fontSize="small" />}
            </IconButton>
          )}
        </TableCell>
        <TableCell>
          <Stack>
            <Stack direction="row" spacing={0.5} alignItems="center">
              <Typography variant="body2" sx={{ fontWeight: 600 }}>{p.name}</Typography>
              {sync?.exists === false && (
                <Tooltip title="연결된 저장소를 찾을 수 없음">
                  <Chip icon={<ErrorIcon />} label="저장소 없음" size="small" color="error" />
                </Tooltip>
              )}
            </Stack>
            <Link
              href={`https://github.com/rayrndsw/${p.id}`}
              target="_blank" rel="noopener" underline="hover"
              onClick={stopProp}
              sx={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 11, color: 'text.secondary' }}
            >
              {p.id}
            </Link>
          </Stack>
        </TableCell>
        <TableCell sx={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 12 }}>
          {milestone ? (
            <Tooltip title={`마일스톤 기준 (${milestone.state === 'closed' ? '릴리스됨' : '진행/계획 중'})${milestone.due_on ? ` · 목표일 ${String(milestone.due_on).slice(0, 10)}` : ''}`}>
              <span>{milestone.tag}</span>
            </Tooltip>
          ) : roadmapVersion ? (
            <Tooltip title="로드맵 계획 버전 (마일스톤 미등록)">
              <span style={{ color: '#999' }}>{roadmapVersion}</span>
            </Tooltip>
          ) : (
            <span style={{ color: '#666' }}>—</span>
          )}
        </TableCell>
        {TIER_ORDER.map((tier, i) => (
          <TableCell
            key={tier}
            sx={{
              borderLeft: i === 0 ? 1 : 0,
              borderRight: i === TIER_ORDER.length - 1 ? 1 : 0,
              borderColor: 'divider',
            }}
          >
            <ModuleIcons modules={modulesByTier(tier)} />
          </TableCell>
        ))}
        <TableCell sx={{ fontSize: 12, color: 'text.secondary' }}>{ownerDisplay}</TableCell>
        <TableCell sx={{ textAlign: 'center' }}>
          <HealthChip health={health} />
        </TableCell>
        <TableCell>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            {p.description || '—'}
          </Typography>
          {p.notes && (
            <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', fontStyle: 'italic' }}>
              {p.notes}
            </Typography>
          )}
          {otherBadges.length > 0 && (
            <Stack direction="row" spacing={0.5} sx={{ mt: 0.5, flexWrap: 'wrap', gap: 0.5 }}>
              {otherBadges.slice(0, 6).map((b, i) => (
                <Tooltip key={i} title={b.alt || b.src}>
                  <img src={b.src} alt={b.alt} style={{ height: 16 }} />
                </Tooltip>
              ))}
            </Stack>
          )}
        </TableCell>
        {/* FEAT-017 — 제품 작업 메뉴: 개별 동기화 · 관리 제외 */}
        <TableCell onClick={stopProp} sx={{ textAlign: 'center' }}>
          <IconButton size="small" onClick={(e) => setMenuEl(e.currentTarget)} aria-label="제품 작업">
            {syncingProduct ? <CircularProgress size={16} /> : <MoreVertIcon fontSize="small" />}
          </IconButton>
          <Menu anchorEl={menuEl} open={!!menuEl} onClose={() => setMenuEl(null)}>
            <MenuItem disabled={syncingProduct} onClick={() => { setMenuEl(null); onSyncProduct?.(p.id); }}>
              <ListItemIcon><SyncIcon fontSize="small" /></ListItemIcon>
              <ListItemText>이 제품 동기화</ListItemText>
            </MenuItem>
            <MenuItem onClick={() => { setMenuEl(null); onExclude?.(p); }} sx={{ color: 'error.main' }}>
              <ListItemIcon><DeleteOutlineIcon fontSize="small" color="error" /></ListItemIcon>
              <ListItemText>관리에서 제외</ListItemText>
            </MenuItem>
          </Menu>
        </TableCell>
      </TableRow>

      {hasSw && (
        <TableRow>
          <TableCell sx={{ py: 0, borderBottom: open ? undefined : 'none' }} colSpan={TOTAL_COLS}>
            <Collapse in={open} timeout="auto" unmountOnExit>
              <SoftwareChildren softwares={maniSw} swMap={swMap} syncingSw={syncingSw} onSyncSw={onSyncSw} />
            </Collapse>
          </TableCell>
        </TableRow>
      )}
    </Fragment>
  );
}

// FEAT-017 — 제품 하위 SW 목록. 출처는 제품 구성 정의(mani.yaml) 파생 목록(softwares).
// 버전·신호·담당은 공용 SW 정보(softwares.yaml, swMap)에 같은 id 가 있으면 보강, 없으면 구성 정의의 설명만.
function SoftwareChildren({ softwares, swMap, syncingSw, onSyncSw }) {
  return (
    <Box sx={{ m: 1, ml: 5 }}>
      <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>
        포함 소프트웨어 ({softwares.length}) · 구성 정의(mani.yaml) 기준
      </Typography>
      <Table size="small" sx={{ mt: 0.5, bgcolor: 'action.hover', borderRadius: 1 }}>
        <TableHead>
          <TableRow>
            <TableCell sx={{ width: 260 }}>SW</TableCell>
            <TableCell sx={{ width: 70, textAlign: 'center' }}>신호</TableCell>
            <TableCell sx={{ width: 120 }}>Latest</TableCell>
            <TableCell sx={{ width: 110 }}>배포일</TableCell>
            <TableCell sx={{ width: 56, textAlign: 'center', whiteSpace: 'nowrap' }}>동기화</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {softwares.map((item) => {
            const swId = item.id;
            const sw = swMap.get(swId);
            const latest = sw?.versions?.latest;
            const registered = !!sw;
            const busy = syncingSw?.has(swId);
            return (
              <TableRow key={swId}>
                <TableCell>
                  <Link
                    href={`https://github.com/rayrndsw/${swId}`}
                    target="_blank" rel="noopener" underline="hover"
                    sx={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 12 }}
                  >
                    {swId}
                  </Link>
                  {item.desc && (
                    <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>{item.desc}</Typography>
                  )}
                </TableCell>
                <TableCell sx={{ textAlign: 'center' }}>
                  {sw ? (sigEmoji[sw.signal] || sigEmoji.white) : (
                    <Tooltip title="공용 SW 정보 미등록 (구성 정의에서만 파생)"><span style={{ color: '#999', fontSize: 11 }}>—</span></Tooltip>
                  )}
                </TableCell>
                <TableCell sx={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 12 }}>
                  {latest?.tag || <span style={{ color: '#666' }}>—</span>}
                </TableCell>
                <TableCell sx={{ fontSize: 12, color: 'text.secondary' }}>
                  {latest?.published ? String(latest.published).slice(0, 10) : '—'}
                </TableCell>
                <TableCell sx={{ textAlign: 'center' }}>
                  {registered && (
                    <Tooltip title={busy ? '동기화 중...' : '이 SW 정보 동기화'}>
                      <span>
                        <IconButton size="small" onClick={() => onSyncSw?.(swId)} disabled={busy}>
                          {busy ? <CircularProgress size={14} /> : <RefreshIcon fontSize="small" />}
                        </IconButton>
                      </span>
                    </Tooltip>
                  )}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </Box>
  );
}

function RegisterDialog({ open, onClose, members = [], onSuccess }) {
  const [url, setUrl] = useState('');
  const [owner, setOwner] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const reset = () => { setUrl(''); setOwner(''); setResult(null); setError(null); };
  const handleClose = () => { if (!busy) { reset(); onClose(); } };

  const handleSubmit = async () => {
    setBusy(true); setError(null); setResult(null);
    try {
      const res = await fetch('/api/products/register', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ repoUrl: url, owner: owner || undefined }),
      });
      const data = await res.json();
      if (!res.ok || !data.ok) {
        setError(data.validation?.errors?.join('\n') || data.error || data.detail || '등록 실패');
        setResult(data);
      } else {
        setResult(data);
      }
    } catch (e) { setError(e.message); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>제품 등록</DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
          제품 repo URL을 입력하면 다음 3가지를 검증합니다:
          <br />① <code>prd-*</code> prefix · ② GitHub repo 존재 · ③ 중복 등록 여부
          <br /><em style={{ fontSize: 11, opacity: 0.7 }}>(status/dashboard.md 는 옵션 — 있으면 sync 활용, 없어도 등록 허용)</em>
        </Typography>
        <TextField
          label="Repo URL"
          placeholder="https://github.com/rayrndsw/prd-xxx 또는 prd-xxx"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          fullWidth
          autoFocus
          sx={{ mb: 2 }}
        />
        <ContactSelector
          value={owner}
          onChange={(v) => setOwner(v || '')}
          members={members}
          label="담당자 (선택)"
          size="medium"
        />
        {error && <Alert severity="error" sx={{ mt: 2, whiteSpace: 'pre-line' }}>{error}</Alert>}
        {result?.ok && (
          <Alert severity="success" sx={{ mt: 2 }}>
            ✅ main 직접 반영됨: {result.commitUrl ? (
              <Link href={result.commitUrl} target="_blank">commit {(result.commitSha || '').slice(0, 7)}</Link>
            ) : (
              <code>{(result.commitSha || '').slice(0, 7)}</code>
            )}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={busy}>닫기</Button>
        {result?.ok ? (
          <Button variant="contained" onClick={() => { onSuccess?.(); handleClose(); }}>완료</Button>
        ) : (
          <Button variant="contained" onClick={handleSubmit} disabled={busy || !url.trim()}>
            {busy ? '검증·등록 중...' : '검증 후 등록'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
